import React, { useState, useRef, useCallback, DragEvent, ClipboardEvent, useEffect } from "react";
import styled from "styled-components";
import Colors from "../global/Colors";
import Fonts from "../global/Fonts";
import Icon from "../global/Icon";
import ButtonMicro from "./ButtonMicro";
import Tooltip from "./Tooltip";
import LoadingSpinner from "./LoadingSpinner";
import Button from "./Button";

const Container = styled.div<{ $isDragging: boolean, $hasError: boolean, $size?: string, $isHovering: boolean, $hasImage: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    // max-width: ${props => props.$size === "large" ? "300px" : props.$size === "small" ? "150px" : "200px"};
    aspect-ratio: 1 / 1;
    min-height: ${props => props.$size === "large" ? "300px" : props.$size === "small" ? "150px" : "200px"};
    border-radius: 6px;
    overflow: hidden;
    background-color: ${Colors.white};
    border: ${props => props.$hasImage ? "1px solid " + Colors.grey1 : "2px dashed " + (props.$hasError ? Colors.red : props.$isDragging ? Colors.blue : Colors.grey1)};
    // padding: 10px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${props => props.$hasError ? Colors.red : Colors.grey4};
    }
`;

const Inner = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    margin: 10px;
    gap: 8px;
`;

const DeleteButton = styled.div`
    position: absolute;
    top: 6px;
    right: 6px;
`;

const ImagePreview = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const HiddenInput = styled.input`
    display: none;
`;

const UploadText = styled.div`
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-size: 14px;
    line-height: 1.2em;
    color: ${Colors.grey6};
    text-align: center;
    // text-wrap: balance;
    margin: 0 20px;
`;

const ErrorText = styled.div`
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-size: 12px;
    line-height: 1.2em;
    color: ${Colors.red};
    text-align: center;
    margin-top: 5px;
`;

const SpinnerOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
`;

interface ImageUploadProps {
    value?: string;
    onChange: (imageUrl: string) => void;
    onError?: (error: string) => void;
    maxSizeMB?: number;
    acceptedTypes?: string[];
    size?: "small" | "medium" | "large";
    placeholder?: string;
    loading?: boolean;
    button?: {
        text?: string;
        icon?: string;
        color?: string;
        onClick?: (event?: React.MouseEvent | React.TouchEvent) => void;
    };
}

const ImageUpload: React.FC<ImageUploadProps> = ({
    value = "",
    onChange,
    onError,
    maxSizeMB = 5,
    acceptedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"],
    size = "medium",
    placeholder = "Drag & drop, paste, or click to upload",
    loading = false,
    button,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [error, setError] = useState<string>("");
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // useEffect(() => {
    //     console.log('isHovering', isHovering);
    // }, [isHovering]);

    const handleError = (errorMessage: string) => {
        setError(errorMessage);
        if (onError) {
            onError(errorMessage);
        }
    };

    const validateFile = (file: File): boolean => {
        // Check file type
        if (!acceptedTypes.includes(file.type)) {
            handleError(`Invalid file type. Accepted types: ${acceptedTypes.join(", ").replace(/image\//g, "")}`);
            return false;
        }

        // Check file size
        if (file.size > maxSizeMB * 1024 * 1024) {
            handleError(`File too large. Maximum size: ${maxSizeMB}MB`);
            return false;
        }

        setError("");
        return true;
    };

    const processFile = (file: File) => {
        if (validateFile(file)) {
            const reader = new FileReader();
            reader.onloadend = () => {
                onChange(reader.result as string);
            };
            reader.onerror = () => {
                handleError("Error reading file");
            };
            reader.readAsDataURL(file);
        }
    };

    const handleClick = () => {
        if (loading) return;
        inputRef.current?.click();
    };

    const handleDeleteImage = () => {
        onChange(""); // Clear the image
        setError("");
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            processFile(file);
        }
    };

    const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (loading) return;
        setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (loading) return;

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            processFile(files[0]);
        }
    };

    const handlePaste = useCallback((e: ClipboardEvent<HTMLDivElement>) => {
        if (loading) return;
        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const file = items[i].getAsFile();
                if (file) {
                    processFile(file);
                    break;
                }
            }
        }
    }, [loading]);

    // Add event listener for paste on component mount
    React.useEffect(() => {
        const currentContainer = containerRef.current;
        if (currentContainer) {
            currentContainer.addEventListener('paste', handlePaste as unknown as EventListener);
        }

        return () => {
            if (currentContainer) {
                currentContainer.removeEventListener('paste', handlePaste as unknown as EventListener);
            }
        };
    }, [handlePaste]);

    return (
        <>
            <Container
                ref={containerRef}
                $isDragging={isDragging}
                $hasError={!!error}
                $size={size}
                $isHovering={isHovering}
                $hasImage={!!value}
                onClick={handleClick}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                tabIndex={0}
            >
                {loading && (
                    <SpinnerOverlay>
                        <LoadingSpinner size={"medium"} colorMode="light" />
                    </SpinnerOverlay>
                )}
                {value ? (
                    <Inner>
                        <ImagePreview src={value} alt="Uploaded Image" />
                        {isHovering && !loading && (
                            <DeleteButton onClick={(e) => e.stopPropagation()}>
                                <Tooltip text="Delete" colorMode="dark" position="top">
                                    <ButtonMicro padding='4px' colorMode='red' icon='delete' onClick={handleDeleteImage} />
                                </Tooltip>
                            </DeleteButton>
                        )}
                    </Inner>
                ) : (
                    <Inner>
                        <Icon icon="image" size={48} color={Colors.grey6} />
                        <UploadText>{placeholder}</UploadText>
                        {button && (
                            // <Button 
                            //     size="micro"
                            //     text={button.text}
                            //     icon={button.icon}
                            //     color={button.color || "white"}
                            //     onClick={(e) => {
                            //         e?.stopPropagation();
                            //         if (button.onClick) {
                            //             button.onClick(e);
                            //         } else {
                            //             handleClick();
                            //         }
                            //     }}
                            // />
                            <ButtonMicro
                                padding='4px'
                                colorMode='light'
                                borderRadius='48px'
                                text={button.text}
                                icon={button.icon}
                                onClick={(e) => {
                                    e?.stopPropagation();
                                    if (button.onClick) {
                                        button.onClick(e);
                                    } else {
                                        handleClick();
                                    }
                                }}
                            />
                        )}
                    </Inner>
                )}
            </Container>
            {error && <ErrorText>{error}</ErrorText>}
            <HiddenInput
                ref={inputRef}
                type="file"
                accept={acceptedTypes.join(",")}
                onChange={handleChange}
            />
        </>
    );
};

export default ImageUpload;